import {
  CircleNotchIcon as Spinner,
  FilesIcon as Files,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { COLUMN_PRESETS, PAGE_SIZE } from "@/lib/metadataConstants";
import { normalizeComparableObject } from "@/lib/compareSelection";
import EvidenceBadge from "@/components/metadata/EvidenceBadge";
import { CompareSelectButton } from "@/components/compare/CompareSelectButton";

// Entities whose rows carry a batch_id (Iter 38). Libraries are derived.
const EVIDENCE_ENTITIES = new Set([
  "objects", "physical-files", "fields", "logical-files", "file-relationships",
  "programs", "program-references", "journal-entries", "journal-receivers",
]);
// Iter 63 — entities whose rows identify a comparable IBM i object.
const COMPARE_ENTITIES = new Set(["objects", "physical-files", "logical-files", "programs", "fields"]);

export default function EntityTable({
  active,
  entity,
  page,
  loading,
  pageNum,
  setPageNum,
  workspaceId,
  workspaceName,
  compareSelection,
}) {
  const columns = COLUMN_PRESETS[active] || [];
  const showEvidence = EVIDENCE_ENTITIES.has(active);
  const showFieldDeps = active === "fields"; // Iter 41
  const showCompare = Boolean(compareSelection?.active) && COMPARE_ENTITIES.has(active);
  const totalPages = page ? Math.max(1, Math.ceil(page.total / PAGE_SIZE)) : 1;
  return (
    <>
      <div className="ml-card overflow-x-auto" data-testid="metadata-table-wrap">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-sm text-[var(--ml-text-secondary)]">
            <Spinner size={18} className="animate-spin mr-2" /> Loading…
          </div>
        ) : !page || page.items.length === 0 ? (
          <EmptyEntity entity={entity} />
        ) : (
          <table className="w-full text-sm" data-testid="metadata-table">
            <thead>
              <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
                {showCompare && (
                  <th className="px-3 py-2 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid="compare-column-header">
                    compare
                  </th>
                )}
                {columns.map((c) => (
                  <th
                    key={c}
                    className="px-3 py-2 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]"
                  >
                    {c.replace(/_/g, " ")}
                  </th>
                ))}
                {showEvidence && (
                  <th
                    className="px-3 py-2 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]"
                    data-testid="evidence-column-header"
                  >
                    evidence
                  </th>
                )}
                {showFieldDeps && (
                  <th className="px-3 py-2 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid="field-deps-column-header">
                    dependencies
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {page.items.map((row, i) => (
                <tr
                  key={row.id}
                  className={`border-b border-[var(--ml-border)] last:border-b-0 ${
                    i % 2 ? "bg-[var(--ml-surface)]/30" : ""
                  }`}
                >
                  {showCompare && (
                    <td className="px-3 py-2 align-top whitespace-nowrap">
                      <CompareSelectButton
                        obj={normalizeComparableObject(row, { entity: active, workspaceId, workspaceName })}
                        selection={compareSelection}
                        compact
                      />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c} className="px-3 py-2 align-top">
                      <CellValue value={row[c]} />
                    </td>
                  ))}
                  {showEvidence && (
                    <td className="px-3 py-2 align-top whitespace-nowrap">
                      <EvidenceBadge
                        batch={row.batch_id ? page.batches?.[row.batch_id] : null}
                        row={row}
                        workspaceId={workspaceId}
                      />
                    </td>
                  )}
                  {showFieldDeps && (
                    <td className="px-3 py-2 align-top whitespace-nowrap">
                      <Link
                        to={`/workspaces/${workspaceId}/metadata/field-dependencies?impact=${encodeURIComponent(row.field_name)}&impact_file=${encodeURIComponent(row.file_name)}&field=${encodeURIComponent(row.field_name)}`}
                        className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline"
                        data-testid={`field-deps-link-${row.id}`}
                      >
                        who touches it →
                      </Link>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {page && page.total > PAGE_SIZE && (
        <Pagination
          pageNum={pageNum}
          totalPages={totalPages}
          setPageNum={setPageNum}
        />
      )}
    </>
  );
}

function CellValue({ value }) {
  if (value === null || value === undefined || value === "") {
    return (
      <span className="text-[var(--ml-text-secondary)]/60">—</span>
    );
  }
  if (typeof value === "boolean") {
    return value ? (
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[#0e6027]">
        yes
      </span>
    ) : (
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
        no
      </span>
    );
  }
  if (typeof value === "number") {
    return <span className="tabular-nums">{value.toLocaleString()}</span>;
  }
  // Detect ISO timestamps so we render them in a friendlier format.
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return (
      <span className="ml-mono text-xs">
        {new Date(value).toLocaleString()}
      </span>
    );
  }
  const s = String(value);
  if (s.length > 80) return <span title={s}>{s.slice(0, 80)}…</span>;
  return s;
}

function EmptyEntity({ entity }) {
  return (
    <div className="p-12 text-center">
      <Files size={36} weight="duotone" color="#0f62fe" className="mx-auto mb-3" />
      <div className="font-medium mb-1">
        No {entity?.label?.toLowerCase()} yet
      </div>
      <p className="text-sm text-[var(--ml-text-secondary)] mb-4 max-w-md mx-auto">
        {entity?.importable ? (
          <>
            Import a <strong>{entity?.source}</strong> CSV export to populate
            this view.
          </>
        ) : (
          <>
            This view auto-populates as you import other sources (e.g. libraries
            get registered when you import <strong>objects</strong>).
          </>
        )}
      </p>
    </div>
  );
}

function Pagination({ pageNum, totalPages, setPageNum }) {
  return (
    <div
      className="flex items-center justify-between text-sm"
      data-testid="metadata-pagination"
    >
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
        Page {pageNum + 1} of {totalPages}
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => setPageNum(Math.max(0, pageNum - 1))}
          disabled={pageNum === 0}
          className="ml-btn-ghost disabled:opacity-40"
          data-testid="pagination-prev"
        >
          ← Prev
        </button>
        <button
          onClick={() => setPageNum(Math.min(totalPages - 1, pageNum + 1))}
          disabled={pageNum >= totalPages - 1}
          className="ml-btn-ghost disabled:opacity-40"
          data-testid="pagination-next"
        >
          Next →
        </button>
      </div>
    </div>
  );
}
